import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useCurrentOrgId, useUserOrgs, setActiveOrgId } from "@/hooks/useSkillOS";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface Props {
  collapsed?: boolean;
}

/**
 * Seletor de organização ativa. Aparece apenas quando o usuário pertence a 2+ orgs.
 * Persiste a escolha em localStorage e invalida queries dependentes.
 */
export function OrgSwitcher({ collapsed = false }: Props) {
  const qc = useQueryClient();
  const { data: orgs = [] } = useUserOrgs();
  const { data: currentOrgId } = useCurrentOrgId();

  if (orgs.length < 2) return null;

  const current = orgs.find((o) => o.org_id === currentOrgId);
  const handleSelect = (orgId: string) => {
    if (orgId === currentOrgId) return;
    setActiveOrgId(orgId);
    qc.invalidateQueries({ queryKey: ["skill-os", "current-org"] });
    qc.invalidateQueries(); // recarrega todos os painéis dependentes
  };

  if (collapsed) {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="mx-auto my-1 h-8 w-8">
                <Building2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">Org: {current?.name ?? "—"}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>Organização</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {orgs.map((o) => (
            <DropdownMenuItem key={o.org_id} onClick={() => handleSelect(o.org_id)}>
              <Check className={cn("h-4 w-4 mr-2", o.org_id === currentOrgId ? "opacity-100" : "opacity-0")} />
              <span className="flex-1 truncate">{o.name}</span>
              {o.active_groups > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">{o.active_groups}g</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="px-3 mt-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between h-8 text-xs font-normal"
          >
            <span className="flex items-center gap-2 truncate">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="truncate">{current?.name ?? "Selecionar org"}</span>
            </span>
            <ChevronsUpDown className="h-3 w-3 opacity-60 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel className="text-xs">Trocar organização</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {orgs.map((o) => (
            <DropdownMenuItem key={o.org_id} onClick={() => handleSelect(o.org_id)} className="text-sm">
              <Check className={cn("h-4 w-4 mr-2", o.org_id === currentOrgId ? "opacity-100" : "opacity-0")} />
              <span className="flex-1 truncate">{o.name}</span>
              {o.active_groups > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {o.active_groups} grupo{o.active_groups > 1 ? "s" : ""}
                </span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
